import { computed, DOCUMENT, effect, inject, Service } from '@angular/core';
import { THEME_BROWSER_COLOR } from '../constants/theme.constant';
import { UserSettingEventConsumerService } from '../events/user-setting/user-setting-event-consumer.service';
import { KalaTheme } from '../models/user-setting.model';

/**
 * Applies the user's theme to the document, and reports it to the screens that
 * render differently under it.
 *
 * Applying it is all this does: the theme is chosen on the تنظیمات page, which
 * saves it alongside every other setting through
 * `UserSettingEventProducerService.saveAppSettings` and reaches this service the
 * way any other setting change does — over the event bus.
 *
 * The theme is a stored user setting like any other, so it lives in
 * `/userSettings` rather than in its own localStorage entry — that way it follows
 * the user across devices, and the cached copy still paints the right theme on the
 * next start before the API answers.
 *
 * The browser's own chrome follows it too. `index.html` can only pick the
 * `theme-color` by the *device's* light/dark mode, which is not this setting: a
 * trader on the dark theme with a light phone got white status and navigation bars.
 * So every `theme-color` tag — whichever media query it answers — is given the
 * theme's colour, and `color-scheme` tells the browser which way its controls and
 * bar icons should read.
 */
@Service()
export class ThemeService {
	private readonly doc = inject(DOCUMENT);
	private readonly userSetting = inject(UserSettingEventConsumerService);

	readonly theme = computed<KalaTheme>(() => this.userSetting.getSetting().theme);
	readonly isDark = computed(() => this.theme() === 'dark');

	constructor() {
		effect(() => {
			const root = this.doc.documentElement;
			root.classList.toggle('dark', this.isDark());
			root.style.colorScheme = this.isDark() ? 'dark' : 'light';

			const browserColor = THEME_BROWSER_COLOR[this.theme()];
			this.doc.querySelectorAll('meta[name="theme-color"]').forEach(meta => {
				meta.setAttribute('content', browserColor);
			});
		});
	}
}
